import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
} from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import { selectStrategies, selectManagers } from "../../../Redux/Reducers/selectors";
import {
  updateLoading,
  updateLoadingMsg,
  addActiveRequests,
  removeActiveRequests,
} from "../../../Redux/Reducers/uiSlice";
import { addStrategy as addStrategyAPI } from "../../../utils/restAPI";
import { setStrategies } from "../../../Redux/Reducers/strategiesSlice";
import { toast } from "react-toastify";
import { StrategySchema } from "../Schema";
import { TAGS_OPTIONS, CATEGORIES_OPTIONS } from "../constants";

const filter = createFilterOptions();

const StrategyForm = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const managers = useSelector(selectManagers);
  const strategies = useSelector(selectStrategies);
  const [formats, setFormats] = useState(["personal"]);

  const handleFormat = (event, newFormat) => {
    if (newFormat !== null) {
      setFormats([newFormat]);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      tags: [],
      categories: [],
    },
    validationSchema: StrategySchema,
    onSubmit: async (values) => {
      const duplicateStrategy = strategies?.find(
        (s) => s.name.toLowerCase() === values.name.toLowerCase()
      );

      if (duplicateStrategy) {
        formik.setFieldError("name", `"${values.name}" already exists, try a new one.`);
        return;
      }

      const requestData = { ...values };

      if (formats[0] === "manager") {
        requestData.owner = `manager#${managers[0]?.managerId}`;
      }

      try {
        dispatch(addActiveRequests("new_strategy"));
        dispatch(updateLoading(true));
        dispatch(updateLoadingMsg("Adding Strategy..."));

        const resp = await addStrategyAPI(requestData);
        if (resp?.error) throw new Error(resp.message || "Failed to add strategy");

        // Add the new strategy to the Redux store so it shows in the list immediately
        const newStrategy = {
          ...requestData,
          ...(resp?.data || resp),
          strategyId: resp?.data?.strategyId || resp?.data?.id || resp?.strategyId || resp?.id,
          id: resp?.data?.id || resp?.data?.strategyId || resp?.id || resp?.strategyId,
          date: Date.now(),
        };
        dispatch(setStrategies([...(strategies || []), newStrategy]));

        toast.success(`${values.name} has been added successfully`);
        onSuccess?.();
      } catch (error) {
        toast.error(error?.message || "Failed to add strategy");
      } finally {
        dispatch(removeActiveRequests("new_strategy"));
        dispatch(updateLoading(false));
        dispatch(updateLoadingMsg(""));
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Stack spacing={3}>
        <TextField
          fullWidth
          variant="outlined"
          label="Strategy Name"
          name="name"
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
        />
        <TextField
          fullWidth
          variant="outlined"
          label="Description"
          name="description"
          multiline
          rows={3}
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.description &&
            Boolean(formik.errors.description)
          }
          helperText={
            formik.touched.description && formik.errors.description
          }
        />

        {/* Tags */}
        <Autocomplete
          multiple
          freeSolo
          options={TAGS_OPTIONS}
          value={formik.values.tags}
          onChange={(event, newValue) => {
            formik.setFieldValue("tags", newValue);
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            const { inputValue } = params;
            if (inputValue.trim() !== "" && !options.includes(inputValue.trim())) {
              filtered.push(inputValue.trim());
            }
            return filtered;
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Tags"
              placeholder={formik.values.tags.length === 0 ? "Select or type a custom tag" : ""}
              error={Boolean(formik.errors.tags)}
              helperText={formik.errors.tags || "Choose from the list or type a custom tag and press Enter"}
              InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
            />
          )}
        />

        {/* Categories */}
        <Autocomplete
          multiple
          freeSolo
          options={CATEGORIES_OPTIONS}
          value={formik.values.categories}
          onChange={(event, newValue) => {
            formik.setFieldValue("categories", newValue);
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            const { inputValue } = params;
            if (inputValue.trim() !== "" && !options.includes(inputValue.trim())) {
              filtered.push(inputValue.trim());
            }
            return filtered;
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Categories"
              placeholder={formik.values.categories.length === 0 ? "Select or type a custom category" : ""}
              error={Boolean(formik.errors.categories)}
              helperText={formik.errors.categories || "Choose from the list or type a custom category and press Enter"}
              InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
            />
          )}
        />

        <FormControl fullWidth>
          <InputLabel shrink sx={{ fontWeight: 600, mb: 1 }}>
            Strategy Owner
          </InputLabel>
          <ToggleButtonGroup
            value={formats[0]}
            exclusive
            onChange={handleFormat}
            aria-label="strategy type"
            sx={{
              mt: 2,
              "& .MuiToggleButton-root": {
                borderRadius: 1,
                textTransform: "none",
                fontWeight: 500,
                px: 3,
                py: 1,
              },
            }}
          >
            <ToggleButton value="personal" aria-label="personal">
              Personal
            </ToggleButton>
            <ToggleButton value="manager" aria-label="manager">
              Manager
            </ToggleButton>
          </ToggleButtonGroup>
        </FormControl>

        <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
          <Button
            type="submit"
            variant="contained"
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              fontWeight: 600,
              minWidth: 120,
            }}
          >
            Create Strategy
          </Button>
        </Box>
      </Stack>
    </form>
  );
};

export default StrategyForm;
